/* eslint-disable @typescript-eslint/no-use-before-define */
import { BottomAction } from '@euler/app/components/BottomAction';
import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { useChangeOrderFlowStep } from '@euler/app/flows/order/functions/useChangeOrderFlowStep';
import { HistoricalServiceInfoBottomSheet } from '@euler/app/flows/order/pages/OrderScreen/components/HistoricalServiceInfo/BottomSheet';
import { useAutoApplyServiceSubjects } from '@euler/app/flows/order/pages/OrderScreen/functions/useAutoApplyServiceSubjects';
import { useCreateChildOrder } from '@euler/app/flows/order/pages/OrderScreen/functions/useCreateChildOrder';
import { useFacadeMediaUpload } from '@euler/app/flows/order/pages/OrderScreen/functions/useFacadeMediaUpload';
import { useOrderFormSections } from '@euler/app/flows/order/pages/OrderScreen/functions/useOrderFormSections';
import { OrderFormMode, OrderFormType } from '@euler/app/flows/order/types';
import { TableView } from '@euler/components/TableView';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBottomActionScroll } from '@euler/functions/useBottomActionScroll';
import { useTask } from '@euler/functions/useTask';
import { useAlert } from '@euler/functions/ux/useAlert';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { StoreConfiguration } from '@euler/model';
import { ResourceAccessScope } from '@euler/model/enum';
import { VehicleServiceInfo } from '@euler/services';
import { useServiceFactory } from '@euler/services/factory';
import { SafeHaptics } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useEffect, useState } from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  ExtendedPropertyContextProvider,
  ExtendedPropertyOptionModal,
} from './components/ExtendedPropertyOptionModal';
import { OrderPageHeaderRight } from './components/HeaderRight';
import { OrderDraftModal } from './components/OrderDraftModal';
import { ReservationAssociationBanner } from './components/ReservationAssociationBanner';
import { NonStandardVinToggle } from './form/components/NonStandardVinToggle';
import { Section } from './form/components/Section';
import { fieldErrorAnchorKeys } from './form/field-error-focus/anchorKeys';
import { FieldErrorFocusContext } from './form/field-error-focus/context';
import { useFieldErrorFocusController } from './form/field-error-focus/useFieldErrorFocusController';
import { useLoadLatestDraft } from './functions/useLoadLatestDraft';
import { usePreventOrderNavigateAway } from './functions/usePreventOrderNavigateAway';
import { useUpdateOrder } from './functions/useUpdateOrder';

export type OrderPageProps = { title: string };

export const OrderScreen = wrapNavigatorScreen<OrderPageProps>(
  () => {
    useToggleAutomaticKeyboard(true);

    const navigation = useNavigation<StackNavigationProp<any>>();

    useLoadLatestDraft();

    usePreventOrderNavigateAway();

    useChangeOrderFlowStep('order-info');

    useEffect(() => {
      navigation.setOptions({
        headerRight: () => <OrderPageHeaderRight />,
      });
    }, [navigation]);

    return (
      <>
        <WiredOrderPageContent />
      </>
    );
  },
  ({ route }) => ({
    title: route.params.title,
  }),
);

const WiredOrderPageContent = memo(() => {
  const appLoading = useAppLoading();
  const alert = useAlert();
  const createChildOrder = useCreateChildOrder();

  const { vehicleInfoService } = useServiceFactory();

  const {
    mode,
    type,
    showTermsOfServiceModal$,
    isSignUnavailable$,
    dirty$,
    formController,
    orderDetail$,
    navigation,
    storeConfiguration,
    basicInfo,
  } = useOrderFlowContext();

  const { form, validateFields } = formController;

  const {
    scrollViewRef,
    contentRef,
    onScroll: onFieldErrorFocusScroll,
    context: fieldErrorFocus,
  } = useFieldErrorFocusController();

  const [dirty] = useBehaviorSubject(dirty$);

  const [detail] = useBehaviorSubject(orderDetail$);

  const updateOrder = useUpdateOrder();

  const isVehicleInfoValid = Boolean(
    form.vin.value &&
      form.vin.error == null &&
      form.licensePlateNo.value &&
      form.licensePlateNo.error == null,
  );

  useFacadeMediaUpload();

  useEffect(() => {
    if (form.vehicleInfo.error) {
      form.vehicleInfo.setError(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.vin.value]);

  const isSignNeeded = () => {
    return (
      mode === 'new' &&
      !orderDetail$.value.signImageUrl &&
      !isSignUnavailable$.value &&
      !storeConfiguration.app?.orderFlow?.skipSign &&
      !(
        storeConfiguration.app?.orderFlow?.fields?.customerSign?.hidden &&
        !storeConfiguration.app?.orderFlow?.fields?.customerSign?.required
      )
    );
  };

  const onSubmit = useTask(async () => {
    const errors = await validateFields();
    if (Object.keys(errors).length) {
      // the offending row is often out of the viewport: buzz right away, then
      // bring it into view and shake it once it lands. not awaited — the
      // scroll must not keep the button in its loading state.
      SafeHaptics.notificationError();
      void fieldErrorFocus.focusFirstError(fieldErrorAnchorKeys(errors));
      return;
    }

    if (mode === 'new') {
      if (type === 'main') {
        if (isSignNeeded()) {
          showTermsOfServiceModal$.next(true);
        } else {
          navigation.navigate('_previewOrder', {});
        }
      } else {
        await createChildOrder();
      }
    } else if (dirty) {
      await updateOrder();
    }
  });

  const { scrollY, onScroll: onBottomActionScroll } = useBottomActionScroll();

  const onScroll = usePersistFn(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      onBottomActionScroll(e);
      onFieldErrorFocusScroll(e);
    },
  );

  const isMediaUploadingOrError = (detail.preInspection ?? [])
    .flatMap(x => x.items)
    .flatMap(x => x.medias)
    .some(
      x => x._source.type === 'local' && x._source.state.status !== 'uploaded',
    );

  const disabled = isMediaUploadingOrError || onSubmit.loading;

  const [showServiceInfoModal, setShowServiceInfoModal] = useState(false);
  const [serviceInfo, setServiceInfo] = useState<VehicleServiceInfo>();

  const onServiceInfoModalDismiss = useCallback(() => {
    setShowServiceInfoModal(false);
  }, []);

  const onViewServiceInfoPress = usePersistFn(async () => {
    if (serviceInfo != null) {
      setShowServiceInfoModal(true);
      return;
    }
    await appLoading.showWithAction(async () => {
      const res = await vehicleInfoService.listVehicleServiceInfo({
        scope: ResourceAccessScope.Org,
        vin: form.vin.value,
        plateno: form.licensePlateNo.value,
        projection: { pendingIssues: true },
      });
      if (res == null) {
        void alert({ title: '未找到车辆历史服务信息' });
        return;
      }
      setServiceInfo(res);
      setShowServiceInfoModal(true);
    });
  });

  useAutoApplyServiceSubjects();

  return (
    <FieldErrorFocusContext.Provider value={fieldErrorFocus}>
      <ExtendedPropertyContextProvider>
        <ScrollView
          ref={scrollViewRef}
          scrollEventThrottle={1}
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          onScroll={onScroll}
          css={`
            flex: 1;
          `}
        >
          {/* the anchor rows measure themselves against this view to work out
            where they sit in the scroll content */}
          <View ref={contentRef}>
            <FormContents
              mode={mode}
              type={type}
              isEntryOrder={basicInfo?.isEntryOrder ?? false}
              hasChildOrders={basicInfo?.hasChildOrders ?? false}
              storeConfiguration={storeConfiguration}
              isVehicleInfoValid={isVehicleInfoValid}
              onViewServiceInfoPress={onViewServiceInfoPress}
            />
          </View>
        </ScrollView>
        {(mode === 'new' || (mode === 'update' && dirty)) && (
          <BottomAction scrollY={scrollY}>
            <BottomAction.Button
              loading={onSubmit.loading}
              variant={disabled ? 'default' : 'primary'}
              text={
                onSubmit.loading
                  ? '请稍候... '
                  : mode === 'update'
                  ? '保存订单信息'
                  : type === 'main'
                  ? isSignNeeded()
                    ? '客户签名'
                    : '预览订单信息'
                  : '创建子订单'
              }
              disabled={disabled}
              onPress={onSubmit.request}
            />
          </BottomAction>
        )}
        <Overlays
          showServiceInfoModal={showServiceInfoModal}
          serviceInfo={serviceInfo}
          onServiceInfoModalDismiss={onServiceInfoModalDismiss}
        />
      </ExtendedPropertyContextProvider>
    </FieldErrorFocusContext.Provider>
  );
});

const FormContents = memo(
  ({
    mode,
    type,
    isEntryOrder,
    hasChildOrders,
    isVehicleInfoValid,
    storeConfiguration,
    onViewServiceInfoPress,
  }: {
    mode: OrderFormMode;
    type: OrderFormType;
    isEntryOrder: boolean;
    hasChildOrders: boolean;
    isVehicleInfoValid: boolean;
    storeConfiguration: StoreConfiguration;
    onViewServiceInfoPress: () => Promise<void>;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const sections = useOrderFormSections({
      storeConfiguration,
      mode,
      type,
      hasChildOrders,
      isEntryOrder,
    });
    return (
      <>
        {/* a standalone card, not a table row — TableView only takes
            sections/items, and a stray child shifts its section indices */}
        {mode === 'new' ? <ReservationAssociationBanner /> : null}
        <TableView
          css={`
            margin-bottom: ${insets.bottom + rpx(98)}px;
          `}
        >
          {sections
            .filter(x => x.fields.length > 0)
            .map((section, index) => (
              <Section
                title={section.name}
                key={section.name}
                titleRight={
                  section.renderTitleRight?.() ??
                  (index === 0 ? (
                    <StackLayout
                      direction="row"
                      alignItems="center"
                      gap={rpx(12)}
                    >
                      {isVehicleInfoValid ? (
                        <LinkButton
                          text="查看服务记录/遗留商机 →"
                          textStyle={{ fontSize: rpx(11), top: -1 }}
                          onPress={onViewServiceInfoPress}
                        />
                      ) : null}
                      {type === 'main' ? <NonStandardVinToggle /> : null}
                    </StackLayout>
                  ) : null)
                }
              >
                {section.fields.map(field => field.render())}
              </Section>
            ))}
        </TableView>
      </>
    );
  },
);

const Overlays = memo(
  ({
    showServiceInfoModal,
    serviceInfo,
    onServiceInfoModalDismiss,
  }: {
    showServiceInfoModal: boolean;
    serviceInfo: VehicleServiceInfo | undefined;
    onServiceInfoModalDismiss: () => void;
  }) => {
    return (
      <>
        <ExtendedPropertyOptionModal />
        <OrderDraftModal />
        <HistoricalServiceInfoBottomSheet
          show={showServiceInfoModal}
          serviceInfo={serviceInfo}
          onDismiss={onServiceInfoModalDismiss}
        />
      </>
    );
  },
);
